import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { SideLoadedBusinessAction } from "actions";

import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import { useHasSaleyardOrDeploymentPermission } from "hooks/useHasPermission";

import {
  getBusinesses,
  getIsStaff,
  selectSideLoadedDataSetsByLabel,
} from "selectors";

import {
  DeleteSideLoadedDataSet,
  useDeleteSideLoadedDataSet,
} from "./useDeleteSideLoadedDataSet";
import type { SideLoadedBusinessRow } from "constants/sideLoadedBusinesses";

// Extends Record to satisfy AgGridTable's context prop, which loses excess
// property checking.
export interface SideLoadedBusinessGridContext extends Record<string, unknown> {
  businessesById: StateModelById<Business>;
  canDeleteDataSet: boolean;
  canIgnore: boolean;
  dataSetsByLabel: Record<string, SideLoadedDataSet>;
  handleSelectAndCompare: (recordId: string) => void;
  handleSetIgnored: (recordId: string, isIgnored: boolean) => void;
  handleDeleteDataSet: DeleteSideLoadedDataSet;
  selectActionLabel: string;
  // Absent on grids with no data set grouping, where there is nothing to edit.
  handleEditTemplate?: (dataSet: SideLoadedDataSet) => void;
  handleRenameDataSet?: (dataSet: SideLoadedDataSet) => void;
}

export const getSideLoadedBusinessRowId = (params: {
  data: SideLoadedBusinessRow;
}): string => params.data.id;

export function useSideLoadedBusinessGridContext(
  handleSelectAndCompare: (recordId: string) => void,
  // The suggestions grid feeds an open form; the manage grid starts a new one.
  selectActionLabel: string = "Use",
  handleEditTemplate?: (dataSet: SideLoadedDataSet) => void,
  handleRenameDataSet?: (dataSet: SideLoadedDataSet) => void,
): SideLoadedBusinessGridContext {
  const businessesById: StateModelById<Business> = useSelector(getBusinesses);
  const dataSetsByLabel = useSelector(selectSideLoadedDataSetsByLabel);
  const dispatch = useDispatch();

  // Records don't carry permissions - curating them is gated at the data set
  // level, which for us is the management feature permission.
  const canIgnore = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureSideLoadedDataManagement,
    SaleyardPermission.featureSideLoadedDataManagement,
  );

  // Matches the upload button - nobody but staff can put a data set here while
  // the feature beds in, so nobody but staff can destroy one either.
  const canDeleteDataSet = useSelector(getIsStaff);

  const handleDeleteDataSet = useDeleteSideLoadedDataSet();

  const handleSetIgnored = useCallback(
    (recordId: string, isIgnored: boolean) => {
      dispatch(SideLoadedBusinessAction.update({ id: recordId, isIgnored }));
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      businessesById,
      canDeleteDataSet,
      canIgnore,
      dataSetsByLabel,
      handleSelectAndCompare,
      handleSetIgnored,
      handleDeleteDataSet,
      selectActionLabel,
      handleEditTemplate,
      handleRenameDataSet,
    }),
    [
      businessesById,
      canDeleteDataSet,
      canIgnore,
      dataSetsByLabel,
      handleSelectAndCompare,
      handleSetIgnored,
      handleDeleteDataSet,
      selectActionLabel,
      handleEditTemplate,
      handleRenameDataSet,
    ],
  );
}
